import { useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import {
  FlexContainer,
  DisplayFormattedDate,
  MoreOptions,
  Pagination,
  Search,
  User
} from 'components/common';
import {
  Box,
  BoxEmpty,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { StatCount } from 'components/common/stats';
import { AccessRequestsAction } from '.';

import type { UserWithBasicData } from 'shared-types';
import type { GetPendingAccessRequestsRes } from 'shared-types/responses';
import type { ApiDataHookUpdateHandler } from 'lib/api/useApiData';

export type AccessRequestObj = {
  action: 'accept' | 'deny';
  id?: number;
  message?: string;
};

export type AccessRequestActions<T = any> = React.FunctionComponent<{
  requestId: number;
  user: UserWithBasicData;
  userData: T;
  update: () => Promise<void>;
}>;

export type AccessRequestBody<T = any> = React.FunctionComponent<{
  user: UserWithBasicData;
  userData: T;
}>;

export type AccessRequestHeader = React.FunctionComponent;

interface Props {
  additionalFilters?: string;
  customTableElements?: {
    Header?: AccessRequestHeader;
    Body?: AccessRequestBody;
    Actions?: AccessRequestActions;
  };
  getUpdateHandler?: (func: ApiDataHookUpdateHandler) => void;
  hideBatchActions?: boolean;
  hideDefaultColumns?: boolean;
  hideDefaultUserActions?: boolean;
  hideTableIfNoRequests?: boolean;
  onRequestAction?: (status: 'accept' | 'deny') => Promise<void> | void;
  searchPlaceholder?: string;
  title?: string;
  url: string;
}

const AccessRequests = <T extends GetPendingAccessRequestsRes>({
  additionalFilters = '',
  customTableElements = { Header: null, Body: null, Actions: null },
  hideBatchActions = false,
  hideDefaultColumns = false,
  hideDefaultUserActions = false,
  hideTableIfNoRequests = false,
  getUpdateHandler,
  onRequestAction,
  searchPlaceholder,
  title,
  url
}: Props) => {
  const { dispatch } = useDispatch();

  const { messages, user } = useReduxState();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, loading, updateData } = useApiData<T>({
    url: `${url}?page=${page}&search=${search}&${additionalFilters}`
  });

  useEffect(() => {
    if (getUpdateHandler) getUpdateHandler(updateData);
  }, [getUpdateHandler, updateData]);

  const handleSearch = ({ search }) => {
    setSearch(search);
    setPage(1);
  };

  const handleRequest = async ({ action, id, message }: AccessRequestObj) => {
    try {
      await api.put(
        `${url}` +
          `${id ? `/${id}` : ''}` +
          `${additionalFilters ? `?${additionalFilters}` : ''}`,
        { action, adminMessage: message }
      );
      await updateData();
      if (onRequestAction) await onRequestAction(action);
      onSuccess(action === 'accept', Boolean(id));
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const onSuccess = (isAccept: boolean, multipleRequests: boolean) => {
    const infoMultiple = isAccept
      ? messages.courseAccessRequestActionAcceptAllMessage
      : messages.courseAccessRequestActionDenyAllMessage;
    const infoSingular = isAccept
      ? messages.usersAddUserSuccess
      : messages.courseAccessRequestActionDenyMessage;

    dispatch.addNotice({
      text: multipleRequests ? infoSingular : infoMultiple,
      type: 'success'
    });
  };

  const { Header, Body, Actions } = customTableElements;

  if (!data?.numberOfRequests && hideTableIfNoRequests && !search) {
    return null;
  }

  return (
    <div>
      <Box
        headerContent={
          <FlexContainer style={{ gap: 10 }}>
            <div style={{ width: 220 }}>
              <Search
                onSearch={handleSearch}
                placeholder={
                  searchPlaceholder
                    ? searchPlaceholder
                    : messages.settingsSearchForUser
                }
                size="header"
              />
            </div>

            {hideBatchActions ? null : (
              <>
                <AccessRequestsAction
                  action="accept"
                  isBatchAction={true}
                  onSubmit={handleRequest}
                  requests={data?.numberOfRequests || 0}
                />

                <MoreOptions category="lighter" styleContentButtons={false}>
                  <AccessRequestsAction
                    action="deny"
                    isBatchAction={true}
                    isDropdown={true}
                    onSubmit={handleRequest}
                    requests={data?.numberOfRequests || 0}
                  />
                </MoreOptions>
              </>
            )}
          </FlexContainer>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={
          <>
            {title || messages.courseAccessRequest}
            {data?.numberOfRequests > 0 ? (
              <StatCount
                isLarge={true}
                style={{ display: 'inline-block', marginLeft: 5 }}
                value={data?.numberOfRequests}
              />
            ) : null}
          </>
        }
      >
        {data?.requests?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                {hideDefaultColumns ? null : (
                  <>
                    <BoxTableCell>{messages.commonUser}</BoxTableCell>
                    <BoxTableCell>
                      {messages.courseAccessRequestDate}
                    </BoxTableCell>
                  </>
                )}

                {Header ? <Header /> : null}

                <BoxTableCell sticky={true} stickyAnchor="right"></BoxTableCell>
              </BoxTableHeader>

              {data.requests.map((request: any) => (
                <BoxTableRow key={request.id}>
                  {hideDefaultColumns ? null : (
                    <>
                      <BoxTableCell>
                        <User enableProfileLink={true} user={request.user} />
                      </BoxTableCell>

                      <BoxTableCell icon={['far', 'calendar-alt']}>
                        <DisplayFormattedDate date={request.requestTime} />
                      </BoxTableCell>
                    </>
                  )}

                  {Body ? (
                    <Body user={request.user} userData={request.data || null} />
                  ) : null}

                  <BoxTableCell
                    shrink={true}
                    sticky={true}
                    stickyAnchor="right"
                    stickyBackgroundColor="white"
                  >
                    <MoreOptions
                      renderInPortal={true}
                      styleContentButtons={false}
                    >
                      {Actions ? (
                        <Actions
                          requestId={request.id}
                          user={request.user}
                          userData={request.data}
                          update={updateData}
                        />
                      ) : null}

                      {request.user?.id === user?.id ||
                      hideDefaultUserActions ? null : (
                        <>
                          <AccessRequestsAction
                            action="accept"
                            id={request.id}
                            isBatchAction={false}
                            isDropdown={true}
                            onSubmit={handleRequest}
                          />
                          <AccessRequestsAction
                            action="deny"
                            id={request.id}
                            isBatchAction={false}
                            isDropdown={true}
                            onSubmit={handleRequest}
                          />
                        </>
                      )}
                    </MoreOptions>
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <BoxEmpty
            align="center"
            padding={20}
            text={
              search
                ? messages.accessRequestsSearchNotFound
                : messages.courseAccessRequestEmpty
            }
          />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 0}
        onPageChange={({ page }) => setPage(page)}
      />
    </div>
  );
};

export default AccessRequests;
